import { useQueryClient } from '@tanstack/react-query';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { SubscriptionType } from '../../types/plan.types';
import { useIdempotentAppMutation } from '../use-idempotent-app-mutation.hook';

export type CreateSubscriptionOfferDto = {
  companyDomain: string;
  companyName: string;
  docusignEnvelopeLimit?: null | number;
  isMonthlyEnabled: boolean;
  monthlyPriceInCents?: null | number;
  planType: SubscriptionType;
  promoCode?: null | string;
  recipients: string[];
  sendEmail: boolean;
  stakeholdersLimit: number;
  yearlyPriceInCents?: null | number;
};

export type CreateSubscriptionOfferResponse = {
  id: string;
  link: string;
  slug: string;
};

// tenant: global — a subscription offer is created before any company exists, so it is not scoped
// to companies/:companyId; the created offer surfaces on the global companies list once claimed.
export const useCreateSubscriptionOffer = () => {
  const queryClient = useQueryClient();

  const {
    mutate: createSubscriptionOffer,
    mutateAsync: createSubscriptionOfferAsync,
    isPending,
  } = useIdempotentAppMutation([QueryKey.CREATE_SUBSCRIPTION_OFFER], {
    mutationFn: (data: CreateSubscriptionOfferDto, { headers }) =>
      ApiService.post<CreateSubscriptionOfferResponse, CreateSubscriptionOfferDto>(
        { endpoint: BackendRoute.SUBSCRIPTION_OFFERS },
        { body: data, headers },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.GET_COMPANIES] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SUBSCRIPTION_OFFERS] });
    },
  });

  return { createSubscriptionOffer, createSubscriptionOfferAsync, isPending };
};
